import { zodResolver } from "@hookform/resolvers/zod";
import {
  IconAdjustmentsHorizontal,
  IconArrowRight,
  IconChartLine,
  IconClock,
  IconRefresh,
} from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { addDays } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ForecastCharts } from "@/components/budget-planning/forecast-charts";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import {
  BUDGET_PERCENT_MAX,
  BUDGET_PERCENT_MIN,
  type BudgetAssumptions,
  budgetCurve,
  forecastBudget,
} from "@/lib/budget-planning";
import { requireModule } from "@/lib/module-access";

export const Route = createFileRoute("/_authed/budget-planning")({
  beforeLoad: () => requireModule("dashboard"),
  component: BudgetPlanningPage,
});

const defaultAssumptions: BudgetAssumptions = {
  budget: 1_000,
  cpc: 1,
  conversionRate: 10,
  averageOrderValue: 100,
  organicOrderShare: 80,
  nonAdCostRate: 60,
};

interface BudgetFormValues {
  budget: number;
}

const metrics = [
  { key: "budget", label: "metricBudget", color: "var(--gray-900)" },
  { key: "totalSales", label: "metricTotalSales", color: "var(--brand-9)" },
  { key: "totalProfit", label: "metricTotalProfit", color: "var(--green-11)" },
  { key: "totalSpend", label: "metricTotalSpend", color: "var(--amber-11)" },
  { key: "tacos", label: "metricTacos", color: "var(--gray-600)" },
  { key: "adSpend", label: "metricAdSpend", color: "var(--amber-11)" },
  { key: "adSales", label: "metricAdSales", color: "var(--brand-9)" },
  { key: "totalOrders", label: "metricTotalOrders", color: "var(--teal-11)" },
] as const;

function BudgetPlanningPage(): React.ReactElement {
  const { t, i18n } = useTranslation("budgetPlanning");
  const numberLocale = i18n.language === "zh-CN" ? "zh-CN" : "en-US";
  const formatMoney = (value: number, fractionDigits = 0): string =>
    new Intl.NumberFormat(numberLocale, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: fractionDigits,
      minimumFractionDigits: fractionDigits,
    }).format(value);
  const formatCount = (value: number): string =>
    new Intl.NumberFormat(numberLocale, { maximumFractionDigits: 0 }).format(value);
  const formatPercent = (value: number): string => `${value.toFixed(2)}%`;
  const [assumptions, setAssumptions] = useState<BudgetAssumptions | null>(null);
  const [adjustedBudget, setAdjustedBudget] = useState(1_200);
  const [budgetText, setBudgetText] = useState("1200");
  const [generating, setGenerating] = useState(false);
  const [showCalculation, setShowCalculation] = useState(false);
  const generationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (generationTimer.current) clearTimeout(generationTimer.current);
    },
    [],
  );

  const positive = z
    .number({ invalid_type_error: t("validationPositive") })
    .gt(0, t("validationPositive"));
  const schema = z.object({
    budget: positive.max(10_000_000, t("validationBudgetMax")),
  });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm<BudgetFormValues>({
    defaultValues: { budget: defaultAssumptions.budget },
    resolver: zodResolver(schema),
  });
  const enteredBudget = watch("budget");
  const baseline = useMemo(() => assumptions && forecastBudget(assumptions), [assumptions]);
  const current = useMemo(
    () => assumptions && forecastBudget(assumptions, adjustedBudget),
    [assumptions, adjustedBudget],
  );
  const curve = useMemo(() => (assumptions ? budgetCurve(assumptions) : []), [assumptions]);
  const percent = assumptions ? (adjustedBudget / assumptions.budget) * 100 : 120;
  const rangeError =
    assumptions &&
    (!Number.isFinite(Number(budgetText)) ||
      Number(budgetText) < assumptions.budget * (BUDGET_PERCENT_MIN / 100) ||
      Number(budgetText) > assumptions.budget * (BUDGET_PERCENT_MAX / 100));
  const today = new Date();
  const dateFormat = new Intl.DateTimeFormat(numberLocale, { month: "2-digit", day: "2-digit" });
  const period = t("period", {
    start: dateFormat.format(addDays(today, 1)),
    end: dateFormat.format(addDays(today, 7)),
  });

  function generate(values: BudgetFormValues): void {
    setGenerating(true);
    generationTimer.current = setTimeout(() => {
      setAssumptions({ ...defaultAssumptions, budget: values.budget });
      const initialSelectedBudget = Math.round(values.budget * 1.2 * 100) / 100;
      setAdjustedBudget(initialSelectedBudget);
      setBudgetText(String(initialSelectedBudget));
      setGenerating(false);
    }, 350);
  }

  function changePercent(nextPercent: number): void {
    if (!assumptions) return;
    const value = Math.round(((assumptions.budget * nextPercent) / 100) * 100) / 100;
    setAdjustedBudget(value);
    setBudgetText(String(value));
  }

  return (
    <div className="w-full px-6 pt-8 pb-24 sm:px-8">
      <div className="mx-auto flex w-full max-w-[1180px] flex-col gap-6">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-[-0.03em]">{t("title")}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{t("subtitle")}</p>
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-line px-3 py-2 text-[13px]">
            <IconClock className="size-4 shrink-0" stroke={1.75} aria-hidden />
            {period}
          </div>
        </header>

        {!assumptions && !generating ? (
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1.4fr)_minmax(300px,1fr)]">
            <form onSubmit={handleSubmit(generate)} noValidate className="flex flex-col gap-6">
              <div>
                <h2 className="text-base font-semibold">{t("setupTitle")}</h2>
                <p className="mt-2 text-[13px] text-muted-foreground">{t("setupHint")}</p>
                <label className="mt-3 flex h-[82px] items-center gap-3 rounded-[10px] border border-control-border bg-surface px-5 shadow-[0_0_0_3px_var(--color-subtle)] focus-within:border-ring">
                  <span className="text-[28px] text-muted-foreground">$</span>
                  <input
                    {...register("budget", { valueAsNumber: true })}
                    type="number"
                    inputMode="decimal"
                    min="0.01"
                    max="10000000"
                    step="0.01"
                    aria-label={t("budget7d")}
                    aria-invalid={Boolean(errors.budget)}
                    className="min-w-0 flex-1 bg-transparent text-[36px] tracking-[-0.04em] outline-none"
                  />
                  <span className="whitespace-nowrap text-[13px] text-muted-foreground">
                    USD / 7
                  </span>
                </label>
                {errors.budget ? (
                  <p className="mt-2 text-xs text-destructive-foreground" role="alert">
                    {errors.budget.message}
                  </p>
                ) : (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {t("dailyAverage", {
                      amount: formatMoney(
                        Number.isFinite(enteredBudget) ? enteredBudget / 7 : 0,
                        2,
                      ),
                    })}
                  </p>
                )}
              </div>
              <div className="flex items-center justify-between gap-4 pt-1">
                <p className="text-xs text-muted-foreground">{t("estimateNotice")}</p>
                <Button type="submit" className="px-5">
                  {t("generate")}
                  <IconArrowRight className="size-4" stroke={1.75} aria-hidden />
                </Button>
              </div>
            </form>
            <aside className="self-start rounded-xl bg-subtle p-7">
              <div className="flex size-10 items-center justify-center rounded-[10px] border border-line bg-surface">
                <IconChartLine className="size-[22px]" stroke={1.75} aria-hidden />
              </div>
              <h2 className="mt-5 max-w-[220px] text-[22px] leading-[30px] font-semibold tracking-[-0.025em]">
                {t("explainerTitle")}
              </h2>
              <p className="mt-4 text-[13px] leading-[22px] text-muted-foreground">
                {t("explainerBody")}
              </p>
            </aside>
          </div>
        ) : generating ? (
          <div className="space-y-5" aria-live="polite">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {metrics.map((metric) => (
                <div key={metric.key} className="rounded-lg border border-line p-4">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="mt-4 h-7 w-32" />
                  <Skeleton className="mt-3 h-3 w-24" />
                </div>
              ))}
            </div>
            <div className="rounded-lg border border-line p-6">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="mt-6 h-48 w-full" />
              <p className="mt-4 text-xs text-muted-foreground">{t("generating")}</p>
            </div>
          </div>
        ) : assumptions && baseline && current ? (
          <>
            <section className="flex flex-wrap items-center gap-6 rounded-[10px] bg-subtle p-5">
              <div className="w-[190px] shrink-0">
                <label htmlFor="adjusted-budget" className="text-xs text-muted-foreground">
                  {t("budget7d")}
                </label>
                <div className="mt-2 flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-2">
                  <span className="text-xl font-semibold">$</span>
                  <input
                    id="adjusted-budget"
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    value={budgetText}
                    onChange={(event) => {
                      const text = event.target.value;
                      setBudgetText(text);
                      const value = Number(text);
                      if (value >= assumptions.budget * 0.5 && value <= assumptions.budget * 2) {
                        setAdjustedBudget(value);
                      }
                    }}
                    aria-invalid={Boolean(rangeError)}
                    className="w-full min-w-0 bg-transparent text-[22px] font-semibold outline-none"
                  />
                  <span className="text-xs text-muted-foreground">{percent.toFixed(0)}%</span>
                </div>
                {rangeError ? (
                  <p role="alert" className="mt-1 text-xs text-destructive-foreground">
                    {t("validationAdjustedBudget")}
                  </p>
                ) : null}
              </div>
              <div className="min-w-[220px] flex-1 space-y-3">
                <div className="flex items-center justify-between gap-2 text-[13px]">
                  <label htmlFor="budget-percentage">{t("budgetPercentage")}</label>
                  <span className="text-muted-foreground">
                    {percent === 100
                      ? t("unchanged")
                      : percent > 100
                        ? t("increased", {
                            amount: formatMoney(adjustedBudget - assumptions.budget),
                            percent: (percent - 100).toFixed(0),
                          })
                        : t("decreased", {
                            amount: formatMoney(assumptions.budget - adjustedBudget),
                            percent: (percent - 100).toFixed(0),
                          })}
                  </span>
                </div>
                <Slider
                  id="budget-percentage"
                  aria-label={t("budgetPercentage")}
                  min={BUDGET_PERCENT_MIN}
                  max={BUDGET_PERCENT_MAX}
                  step={1}
                  value={[percent]}
                  onValueChange={(value) =>
                    changePercent(Array.isArray(value) ? (value[0] ?? 100) : value)
                  }
                />
                <div className="flex justify-between text-[11px] text-muted-foreground">
                  <span>50%</span>
                  <span>{t("baseline", { amount: formatMoney(assumptions.budget) })}</span>
                  <span>150%</span>
                  <span>200%</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" onClick={() => changePercent(100)}>
                  <IconRefresh className="size-4" stroke={1.75} aria-hidden />
                  {t("reset")}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    reset({ budget: assumptions.budget });
                    setAssumptions(null);
                  }}
                >
                  <IconAdjustmentsHorizontal className="size-4" stroke={1.75} aria-hidden />
                  {t("editParameters")}
                </Button>
              </div>
            </section>
            <section className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold">{t("forecastTitle")}</h2>
                <span className="text-xs text-muted-foreground">
                  {t("comparedToBaseline", { amount: formatMoney(assumptions.budget) })}
                </span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {metrics.map(({ key, label, color }) => {
                  const value = current[key];
                  const baseValue = baseline[key];
                  const displayedValue =
                    value !== null && key === "totalOrders" ? Math.round(value) : value;
                  const displayedBaseValue =
                    baseValue !== null && key === "totalOrders" ? Math.round(baseValue) : baseValue;
                  const change =
                    displayedValue !== null && displayedBaseValue !== null
                      ? displayedValue - displayedBaseValue
                      : null;
                  const relative =
                    change !== null && displayedBaseValue !== 0 && displayedBaseValue !== null
                      ? (change / Math.abs(displayedBaseValue)) * 100
                      : null;
                  return (
                    <div
                      key={key}
                      className={`min-w-0 rounded-lg border border-line p-4 ${key === "totalProfit" ? "bg-green-2" : "bg-surface"}`}
                    >
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span
                          className="size-1.5 shrink-0 rounded-full"
                          style={{ backgroundColor: color }}
                        />
                        <span>{t(label)}</span>
                      </div>
                      <p className="mt-2 text-[28px] leading-8 font-semibold tracking-[-0.04em] tabular-nums">
                        {displayedValue === null
                          ? "—"
                          : key === "tacos"
                            ? formatPercent(displayedValue)
                            : key === "totalOrders"
                              ? formatCount(displayedValue)
                              : formatMoney(displayedValue)}
                      </p>
                      <p className="mt-1.5 min-h-4 text-[11px] text-muted-foreground">
                        {value === null
                          ? t("tacosMissing")
                          : change === null || Math.abs(change) < 0.005
                            ? t("unchanged")
                            : key === "tacos"
                              ? `${change > 0 ? "+" : ""}${change.toFixed(2)}%`
                              : `${change > 0 ? "+" : "−"}${key === "totalOrders" ? formatCount(Math.abs(change)) : formatMoney(Math.abs(change))} · ${relative !== null && relative > 0 ? "+" : ""}${relative?.toFixed(0) ?? 0}%`}
                      </p>
                    </div>
                  );
                })}
              </div>
            </section>
            <ForecastCharts curve={curve} percent={percent} formatMoney={formatMoney} />
            <footer className="flex flex-wrap items-start justify-between gap-4 text-[11px] leading-5 text-muted-foreground">
              <div>
                <p>
                  {t("assumptions", {
                    cpc: formatMoney(assumptions.cpc, 2),
                    conversion: assumptions.conversionRate,
                    orderValue: formatMoney(assumptions.averageOrderValue),
                    organic: assumptions.organicOrderShare,
                    cost: assumptions.nonAdCostRate,
                  })}
                </p>
                <p>{t("calculationNotice")}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowCalculation((open) => !open)}
                aria-expanded={showCalculation}
                className="rounded-md text-xs text-ink underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {t("calculationDetails")}
              </button>
              {showCalculation ? (
                <div className="w-full rounded-lg border border-line bg-subtle p-4 text-xs leading-5 text-ink">
                  <h3 className="font-semibold">{t("calculationTitle")}</h3>
                  <p className="mt-2">{t("calculationBody")}</p>
                </div>
              ) : null}
            </footer>
          </>
        ) : null}
      </div>
    </div>
  );
}
